// Super-admin: PlayFirst Parent members dashboard.
// /admin/parent-members
//
// Lists every parent_self signup with subscription state, child count,
// WhatsApp status, and a one-click toggle to activate/deactivate after
// confirming a Paystack payment.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Search, RefreshCw, CheckCircle2, XCircle, MessageCircle, Loader2, Users, AlertTriangle, Clock, Heart, Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { v: 'all', l: 'All members' },
  { v: 'active', l: 'Active only' },
  { v: 'inactive', l: 'Inactive only' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

export default function AdminParentMembers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: null, members: [] });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [savingId, setSavingId] = useState(null);
  // Track which row is currently sending an activation nudge — separate
  // from `savingId` so the "Send reminder" spinner and the Activate
  // toggle spinner don't fight each other.
  const [remindingId, setRemindingId] = useState(null);

  // ---- Grant PFP access panel (dual-role therapists / historical users) ----
  const [grantEmail, setGrantEmail] = useState('');
  const [grantPlan, setGrantPlan] = useState('monthly');
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState(null);

  // ---- Unmatched billing events resolver ----
  const [unmatched, setUnmatched] = useState([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [therapistPlan, setTherapistPlan] = useState({}); // { [event_id]: 'starter' | 'professional' | 'enterprise' | 'monthly' | 'annual' | 'lifetime' }
  const PFP_PLANS = ['monthly', 'annual', 'lifetime'];
  const CLINICAL_PLANS = ['starter', 'professional', 'enterprise'];
  const [lookupQuery, setLookupQuery] = useState({}); // { [event_id]: 'partial email or name' }
  const [lookupResults, setLookupResults] = useState({}); // { [event_id]: [user, ...] }
  const [lookupBusy, setLookupBusy] = useState({}); // { [event_id]: bool }

  const auth = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const handleGrantPfpAccess = async () => {
    const email = grantEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter an email address'); return; }
    if (!window.confirm(
      `Grant PlayFirst Parent access to ${email} on the ${grantPlan} plan?\n\n` +
      `This sets parent_subscription_status=active and has_parent_app_access=true. ` +
      `They\'ll see the PlayFirst Parent menu on next sign-in.`
    )) return;
    setGranting(true);
    setGrantResult(null);
    try {
      const r = await axios.post(`${API}/admin/users/grant-pfp-access`, { email, plan: grantPlan }, auth());
      setGrantResult(r.data);
      toast.success(r.data?.message || 'Access granted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not grant access');
    } finally {
      setGranting(false);
    }
  };

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/parent-members`, auth());
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Super-admin only');
        navigate('/');
      } else {
        toast.error("Couldn't load members");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, navigate]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const fetchUnmatched = useCallback(async () => {
    setUnmatchedLoading(true);
    try {
      const r = await axios.get(`${API}/admin/unmatched-billing-events`, auth());
      setUnmatched(r.data?.events || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "Couldn't load unmatched billing events");
      }
    } finally {
      setUnmatchedLoading(false);
    }
  }, [auth]);

  useEffect(() => { fetchUnmatched(); }, [fetchUnmatched]);

  const activateFromUnmatched = async (ev, suggestion) => {
    // Default plan by role: parent_self → 'annual', otherwise → 'starter'.
    // Admin can override via the dropdown.
    const defaultPlan = suggestion.role === 'parent_self' ? 'annual' : 'starter';
    const chosenPlan = (therapistPlan[ev.id] || defaultPlan).toLowerCase();
    const isPfpPlan = PFP_PLANS.includes(chosenPlan);
    if (!isPfpPlan && !CLINICAL_PLANS.includes(chosenPlan)) {
      toast.error('Please pick a valid plan first');
      return;
    }
    const planLabelHuman = isPfpPlan ? `PlayFirst Parent (${chosenPlan})` : `${chosenPlan}`;
    const confirmMsg = isPfpPlan
      ? `Activate PlayFirst Parent for ${suggestion.email} on the ${chosenPlan} plan and close this unmatched event?`
      : `Activate the therapist subscription for ${suggestion.email} on the ${chosenPlan} plan and close this unmatched event?`;
    if (!window.confirm(confirmMsg)) return;
    setResolvingId(ev.id);
    try {
      if (isPfpPlan) {
        // Grant PFP access (works for parent_self AND dual-role therapists)
        await axios.post(
          `${API}/admin/users/grant-pfp-access`,
          { email: suggestion.email, plan: chosenPlan },
          auth(),
        );
        await axios.post(
          `${API}/admin/resolve-billing-event/${ev.id}`,
          { notes: `Activated ${suggestion.email} as ${planLabelHuman}. Paystack ref: ${ev.paystack_reference || 'n/a'}` },
          auth(),
        );
      } else {
        // Clinical therapist subscription activation
        await axios.post(
          `${API}/admin/users/${suggestion.id}/activate-subscription`,
          {
            plan_tier: chosenPlan,
            paystack_reference: ev.paystack_reference || null,
            resolve_event_id: ev.id,
            notes: `Matched via ${suggestion.match_reason}. Amount: ${ev.amount_display || 'n/a'}.`,
          },
          auth(),
        );
      }
      toast.success(`Activated ${suggestion.email} on ${planLabelHuman}`);
      setUnmatched((rows) => rows.filter((r) => r.id !== ev.id));
      fetchMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate');
    } finally {
      setResolvingId(null);
    }
  };

  const dismissUnmatched = async (ev) => {
    const notes = window.prompt(
      `Mark this event as resolved without activating a user?\nOptional note (e.g. "duplicate", "refunded", "test payment"):`,
      'Resolved manually'
    );
    if (notes === null) return;
    setResolvingId(ev.id);
    try {
      await axios.post(
        `${API}/admin/resolve-billing-event/${ev.id}`,
        { notes: notes || 'Resolved manually' },
        auth(),
      );
      toast.success('Event closed');
      setUnmatched((rows) => rows.filter((r) => r.id !== ev.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not close event');
    } finally {
      setResolvingId(null);
    }
  };

  const lookupForEvent = async (eventId) => {
    const q = (lookupQuery[eventId] || '').trim();
    if (q.length < 2) {
      toast.error('Type at least 2 characters');
      return;
    }
    setLookupBusy((b) => ({ ...b, [eventId]: true }));
    try {
      const r = await axios.get(`${API}/admin/users/lookup`, { ...auth(), params: { q } });
      setLookupResults((m) => ({ ...m, [eventId]: r.data?.results || [] }));
      if ((r.data?.results || []).length === 0) {
        toast.info('No users found — try a shorter search or check the spelling');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupBusy((b) => ({ ...b, [eventId]: false }));
    }
  };

  const filtered = useMemo(() => {
    let list = data.members || [];
    if (statusFilter === 'active') {
      list = list.filter((m) => m.subscription_status === 'active');
    } else if (statusFilter === 'inactive') {
      list = list.filter((m) => m.subscription_status !== 'active');
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((m) =>
        (m.name || '').toLowerCase().includes(q)
        || (m.email || '').toLowerCase().includes(q)
        || (m.country || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, search, statusFilter]);

  const toggleStatus = async (m) => {
    const next = m.subscription_status === 'active' ? 'inactive' : 'active';
    const plan = next === 'active'
      ? (m.subscription_plan && m.subscription_plan !== 'grandfathered'
          ? m.subscription_plan
          // Soft prompt: ask which plan they bought before activating.
          : window.prompt('Which plan did this parent purchase? (monthly / annual / lifetime)', 'annual'))
      : null;
    if (next === 'active' && !plan) return;  // user cancelled the prompt
    if (next === 'active' && !['monthly', 'annual', 'lifetime'].includes((plan || '').trim().toLowerCase())) {
      toast.error('Plan must be monthly, annual or lifetime');
      return;
    }
    setSavingId(m.id);
    try {
      const r = await axios.put(
        `${API}/admin/parent-members/${m.id}/subscription`,
        { status: next, plan: next === 'active' ? plan.trim().toLowerCase() : null },
        auth(),
      );
      // Patch in place.
      setData((d) => ({
        ...d,
        members: d.members.map((row) =>
          row.id === m.id
            ? {
                ...row,
                subscription_status: r.data.subscription_status,
                subscription_plan: r.data.subscription_plan,
                subscription_activated_at: r.data.subscription_activated_at,
              }
            : row
        ),
        summary: d.summary ? {
          ...d.summary,
          active: d.members.filter((row) =>
            row.id === m.id ? r.data.subscription_status === 'active' : row.subscription_status === 'active'
          ).length,
          inactive: d.members.filter((row) =>
            row.id === m.id ? r.data.subscription_status !== 'active' : row.subscription_status !== 'active'
          ).length,
        } : d.summary,
      }));
      toast.success(next === 'active' ? `Activated ${m.name || m.email}` : `Deactivated ${m.name || m.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally {
      setSavingId(null);
    }
  };

  // Send a "you signed up, please pick a plan" nudge. Only offered on
  // INACTIVE rows — activating a paid member here would be redundant.
  // The backend soft-rate-limits to one send per 12h; we surface the
  // last-sent timestamp on the button so admins don't spam.
  const sendActivationReminder = async (m) => {
    const label = m.name || m.email;
    const priorCount = m.activation_reminder_count || 0;
    const priorLine = priorCount
      ? `\n\nYou've nudged them ${priorCount} time${priorCount === 1 ? '' : 's'} before.`
      : '';
    if (!window.confirm(
      `Send a payment-reminder email to ${label} (${m.email})?\n\n` +
      `They'll get a warm PFP-voice email with a direct link to /parent-app/pricing.` +
      priorLine
    )) return;

    setRemindingId(m.id);
    try {
      const r = await axios.post(
        `${API}/admin/parent-members/${m.id}/send-activation-reminder`,
        {},
        auth(),
      );
      // Patch the row so the button reflects the fresh timestamp/count
      // without needing a full refetch.
      setData((d) => ({
        ...d,
        members: d.members.map((row) =>
          row.id === m.id
            ? {
                ...row,
                last_activation_reminder_at: r.data.sent_at,
                activation_reminder_count: r.data.reminder_count,
              }
            : row
        ),
      }));
      toast.success(`Reminder sent to ${m.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="parent-members-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Link to="/" className="text-sm text-stone-500 hover:underline">PlayFirst</Link>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 pb-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-stone-900">PlayFirst Parent Members</h1>
              <p className="text-sm text-stone-500">Monitor signups and manually activate paid members.</p>
            </div>
          </div>
        </motion.div>

        {/* Unmatched billing events banner */}
        {data.summary && (data.summary.unmatched_billing_events > 0 || data.summary.cancel_requested > 0) && (
          <div className="mt-6 space-y-3" data-testid="parent-members-banners">
            {data.summary.unmatched_billing_events > 0 && (
              <div
                className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                data-testid="unmatched-billing-banner"
              >
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-900">
                    {data.summary.unmatched_billing_events} unmatched Paystack {data.summary.unmatched_billing_events === 1 ? 'payment' : 'payments'} — these need attention
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                    A customer paid using an email that doesn&apos;t match any PlayFirst account. Check your inbox for the alert email,
                    find the matching user, and activate them manually below — or ask them to forward you the Paystack receipt.
                  </p>
                </div>
              </div>
            )}
            {data.summary.cancel_requested > 0 && (
              <div
                className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
                data-testid="cancel-requested-banner"
              >
                <Clock className="w-5 h-5 text-rose-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-rose-900">
                    {data.summary.cancel_requested} parent{data.summary.cancel_requested === 1 ? '' : 's'} requested cancellation
                  </p>
                  <p className="text-xs text-rose-800 mt-0.5 leading-relaxed">
                    Process the cancellation in Paystack, then click <strong>Deactivate</strong> on their row below to mark it done.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unmatched billing events resolver */}
        {unmatched.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50/40 p-4 sm:p-5" data-testid="unmatched-billing-resolver">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-base font-semibold text-amber-900">
                  Resolve unmatched Paystack payments ({unmatched.length})
                </h2>
                <p className="text-xs text-amber-800 mt-0.5">
                  Suggested account matches are shown below — one click activates them and closes the alert.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchUnmatched}
                disabled={unmatchedLoading}
                className="text-amber-900"
                data-testid="refresh-unmatched-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${unmatchedLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="space-y-3">
              {unmatched.map((ev) => (
                <div
                  key={ev.id}
                  className="rounded-xl bg-white border border-amber-200 p-3 sm:p-4"
                  data-testid={`unmatched-event-${ev.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900 break-all">
                        {ev.customer_email || '(no email)'}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        {ev.event_type} · {ev.amount_display || 'amount unknown'}
                        {ev.paystack_reference && (
                          <> · ref <span className="font-mono">{ev.paystack_reference}</span></>
                        )}
                        {ev.created_at && <> · {fmt(ev.created_at)}</>}
                      </p>
                      <p className="text-[11px] text-stone-400 mt-0.5">{ev.reason}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissUnmatched(ev)}
                      disabled={resolvingId === ev.id}
                      className="text-stone-500 text-xs"
                      data-testid={`dismiss-unmatched-${ev.id}`}
                      title="Mark as resolved without activating a user"
                    >
                      Dismiss
                    </Button>
                  </div>

                  {ev.suggested_matches && ev.suggested_matches.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                        Likely matches
                      </p>
                      {ev.suggested_matches.map((s) => (
                        <div
                          key={s.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2"
                          data-testid={`suggested-match-${ev.id}-${s.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-stone-900 truncate">
                              {s.name || s.email}
                              <span className="ml-2 text-[11px] text-stone-500 font-mono">{s.email}</span>
                            </p>
                            <p className="text-[11px] text-stone-500 mt-0.5">
                              role: <span className="font-medium">{s.role || 'unknown'}</span>
                              {' · '}match: <span className="italic">{s.match_reason}</span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={therapistPlan[ev.id] || (s.role === 'parent_self' ? 'annual' : 'starter')}
                              onValueChange={(v) =>
                                setTherapistPlan((p) => ({ ...p, [ev.id]: v }))
                              }
                            >
                              <SelectTrigger className="h-8 text-xs w-40" data-testid={`plan-select-${ev.id}-${s.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500">
                                    PlayFirst Parent
                                  </SelectLabel>
                                  <SelectItem value="monthly">Monthly (PFP)</SelectItem>
                                  <SelectItem value="annual">Annual (PFP)</SelectItem>
                                  <SelectItem value="lifetime">Lifetime (PFP)</SelectItem>
                                </SelectGroup>
                                <SelectGroup>
                                  <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">
                                    Clinical (therapist)
                                  </SelectLabel>
                                  <SelectItem value="starter">Starter</SelectItem>
                                  <SelectItem value="professional">Professional</SelectItem>
                                  <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => activateFromUnmatched(ev, s)}
                              disabled={resolvingId === ev.id}
                              className="text-xs h-8"
                              data-testid={`activate-match-${ev.id}-${s.id}`}
                            >
                              {resolvingId === ev.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Activate
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Manual search-by-email/name — always available, especially useful when
                      no fuzzy match was found. Lets the admin find the user in two seconds. */}
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">
                      Find the user manually
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        value={lookupQuery[ev.id] || ''}
                        onChange={(e) =>
                          setLookupQuery((m) => ({ ...m, [ev.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            lookupForEvent(ev.id);
                          }
                        }}
                        placeholder="Search by email or name (e.g. marika, mavro, @practice.co)"
                        className="h-9 rounded-lg flex-1 min-w-[200px] text-sm"
                        data-testid={`lookup-input-${ev.id}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => lookupForEvent(ev.id)}
                        disabled={lookupBusy[ev.id]}
                        className="h-9 rounded-lg text-xs"
                        data-testid={`lookup-btn-${ev.id}`}
                      >
                        {lookupBusy[ev.id] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <Search className="w-3.5 h-3.5 mr-1" /> Search
                          </>
                        )}
                      </Button>
                    </div>

                    {Array.isArray(lookupResults[ev.id]) && lookupResults[ev.id].length > 0 && (
                      <div className="mt-2 space-y-2">
                        {lookupResults[ev.id].map((u) => (
                          <div
                            key={u.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50/40 border border-emerald-200 px-3 py-2"
                            data-testid={`lookup-result-${ev.id}-${u.id}`}
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-stone-900 truncate">
                                {u.name || u.email}
                                <span className="ml-2 text-[11px] text-stone-500 font-mono">{u.email}</span>
                              </p>
                              <p className="text-[11px] text-stone-500 mt-0.5">
                                role: <span className="font-medium">{u.role || 'unknown'}</span>
                                {u.subscription_status && (
                                  <> · status: <span className="italic">{u.subscription_status}</span></>
                                )}
                                {u.plan && <> · plan: <span className="italic">{u.plan}</span></>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={therapistPlan[ev.id] || (u.role === 'parent_self' ? 'annual' : 'starter')}
                                onValueChange={(v) =>
                                  setTherapistPlan((p) => ({ ...p, [ev.id]: v }))
                                }
                              >
                                <SelectTrigger className="h-8 text-xs w-40" data-testid={`plan-select-lookup-${ev.id}-${u.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500">
                                      PlayFirst Parent
                                    </SelectLabel>
                                    <SelectItem value="monthly">Monthly (PFP)</SelectItem>
                                    <SelectItem value="annual">Annual (PFP)</SelectItem>
                                    <SelectItem value="lifetime">Lifetime (PFP)</SelectItem>
                                  </SelectGroup>
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">
                                      Clinical (therapist)
                                    </SelectLabel>
                                    <SelectItem value="starter">Starter</SelectItem>
                                    <SelectItem value="professional">Professional</SelectItem>
                                    <SelectItem value="enterprise">Enterprise</SelectItem>
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                onClick={() =>
                                  activateFromUnmatched(ev, { ...u, match_reason: 'manual-lookup' })
                                }
                                disabled={resolvingId === ev.id}
                                className="text-xs h-8"
                                data-testid={`activate-lookup-${ev.id}-${u.id}`}
                              >
                                {resolvingId === ev.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Activate
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(!ev.suggested_matches || ev.suggested_matches.length === 0) && !lookupResults[ev.id] && (
                      <p className="mt-2 text-[11px] text-stone-500 italic">
                        Tip: ask the payer what email they signed up to PlayFirst with, then search above.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary cards */}
        {data.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <SummaryCard label="Total signups" value={data.summary.total} testid="summary-total" />
            <SummaryCard label="Active" value={data.summary.active} accent="text-emerald-700" testid="summary-active" />
            <SummaryCard label="Inactive" value={data.summary.inactive} accent="text-stone-500" testid="summary-inactive" />
            <SummaryCard label="WhatsApp verified" value={data.summary.whatsapp_verified} accent="text-[#7C6F9C]" testid="summary-whatsapp" />
          </div>
        )}

        {/* Grant PFP access panel — for dual-role therapists or any user whose
            webhook missed. Looks up by email across all roles. */}
        <div
          className="mt-6 rounded-2xl border border-[#7C6F9C]/25 bg-[#7C6F9C]/5 p-5"
          data-testid="grant-pfp-access-panel"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/15 text-[#7C6F9C] flex items-center justify-center shrink-0">
              <Heart className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-stone-900">Grant PlayFirst Parent access</h2>
              <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">
                Manually activate PFP for a therapist who&apos;s paid but didn&apos;t auto-activate
                (e.g. dual-role colleagues), or anyone whose Paystack webhook missed.
                Works for any role.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">User email</label>
              <Input
                type="email"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="e.g. fourie.kayla8@gmail.com"
                className="h-10 rounded-xl"
                data-testid="grant-pfp-email-input"
              />
            </div>
            <div className="w-full sm:w-40">
              <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Plan</label>
              <Select value={grantPlan} onValueChange={setGrantPlan}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="grant-pfp-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleGrantPfpAccess}
              disabled={granting || !grantEmail.trim()}
              className="h-10 bg-[#7C6F9C] hover:bg-[#6a5d8b] text-white rounded-xl px-5"
              data-testid="grant-pfp-submit-btn"
            >
              {granting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
              Grant access
            </Button>
          </div>
          {grantResult && (
            <div
              className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900"
              data-testid="grant-pfp-success"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{grantResult.message}</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    Role: <span className="font-mono">{grantResult.role}</span> ·
                    Plan: <span className="font-mono">{grantResult.parent_subscription_plan}</span> ·
                    Activated: {fmt(grantResult.parent_subscription_activated_at)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-testid="parent-members-toolbar">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 w-4 h-4 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, country…"
              className="pl-9 h-10 rounded-full"
              data-testid="parent-members-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-44 rounded-full" data-testid="parent-members-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchMembers} className="rounded-full" data-testid="parent-members-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white overflow-x-auto" data-testid="parent-members-table">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-stone-500" data-testid="parent-members-empty">No members match these filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Parent</th>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Signed up</th>
                  <th className="text-center px-4 py-3">Children</th>
                  <th className="text-center px-4 py-3">WhatsApp</th>
                  <th className="text-center px-4 py-3">Subscription</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="border-t border-stone-100 hover:bg-stone-50/50" data-testid={`parent-member-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-900">{m.name || '—'}</div>
                      <div className="text-xs text-stone-500">{m.email}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-700">{m.country || '—'}</td>
                    <td className="px-4 py-3 text-stone-700">{fmt(m.created_at)}</td>
                    <td className="px-4 py-3 text-center text-stone-700">{m.child_count}</td>
                    <td className="px-4 py-3 text-center">
                      {m.whatsapp_verified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <MessageCircle className="w-3.5 h-3.5" /> {m.whatsapp_reminder_frequency || 'weekly'}
                        </span>
                      ) : (
                        <span className="text-xs text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.subscription_status === 'active' ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active{m.subscription_plan ? ` · ${m.subscription_plan}` : ''}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-stone-100 text-stone-600 hover:bg-stone-100">
                          <XCircle className="w-3 h-3 mr-1" /> Inactive
                        </Badge>
                      )}
                      {m.cancel_requested_at && (
                        <div className="mt-1">
                          <Badge
                            className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-[10px]"
                            data-testid={`cancel-pending-badge-${m.id}`}
                            title={m.cancel_reason || 'Cancellation requested'}
                          >
                            <Clock className="w-3 h-3 mr-1" /> Cancel requested
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Only show the reminder button for INACTIVE parents.
                            The tooltip surfaces when they were last nudged so
                            admins can pace themselves. */}
                        {m.subscription_status !== 'active' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full text-xs border-[#7C6F9C]/40 text-[#7C6F9C] hover:bg-[#7C6F9C]/5"
                            onClick={() => sendActivationReminder(m)}
                            disabled={remindingId === m.id || savingId === m.id}
                            data-testid={`parent-member-remind-${m.id}`}
                            title={
                              m.last_activation_reminder_at
                                ? `Last nudged: ${fmt(m.last_activation_reminder_at)} · ${m.activation_reminder_count || 0} total`
                                : 'Never nudged'
                            }
                          >
                            {remindingId === m.id
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <Mail className="w-3 h-3 mr-1" />}
                            {m.activation_reminder_count > 0 ? 'Remind again' : 'Send reminder'}
                          </Button>
                        )}
                        <Button
                          variant={m.subscription_status === 'active' ? 'outline' : 'default'}
                          size="sm"
                          className={`rounded-full text-xs ${m.subscription_status === 'active' ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                          onClick={() => toggleStatus(m)}
                          disabled={savingId === m.id || remindingId === m.id}
                          data-testid={`parent-member-toggle-${m.id}`}
                        >
                          {savingId === m.id && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                          {m.subscription_status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, accent = 'text-stone-900', testid }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className={`text-3xl font-serif mt-1 ${accent}`}>{value}</p>
    </div>
  );
}
